import { useState } from "react";
import { Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUserRole } from "@/hooks/useUserRole";
import { TaxReviewTab } from "./TaxReviewTab";
import { usePayouts } from "./payouts/usePayouts";
import { PayableTable } from "./payouts/PayableTable";
import { RecentBatchesTable } from "./payouts/RecentBatchesTable";
import { MarkPaidDialog } from "./payouts/MarkPaidDialog";
import { formatCommissionAmount, maskReferredUserId } from "@/lib/affiliateDashboard";
import type { PayableRow } from "./payouts/types";

export function PayoutsTab() {
  const { isSuperAdmin } = useUserRole();
  const s = usePayouts();
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmOne, setConfirmOne] = useState<PayableRow | null>(null);

  return (
    <Tabs defaultValue="payouts" className="space-y-4">
      <TabsList>
        <TabsTrigger value="payouts">Payouts</TabsTrigger>
        <TabsTrigger value="tax">Tax review</TabsTrigger>
      </TabsList>

      <TabsContent value="tax">
        <TaxReviewTab />
      </TabsContent>

      <TabsContent value="payouts" className="space-y-6">
        {isSuperAdmin ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">Run Stripe payouts</h3>
                <Badge variant="outline" className="uppercase">{s.env}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Triggers the same job that runs daily at 04:33 UTC. Pushes funds to every affiliate
                whose Connect account is onboarded and whose balance clears the per-currency floor.
              </p>
            </div>
            <Button
              onClick={() => setConfirmAll(true)}
              disabled={s.running !== null}
            >
              <Send className="mr-2 h-4 w-4" />
              {s.running === "all" ? "Running…" : "Run payouts now"}
            </Button>
          </div>
        ) : null}

        <PayableTable
          payable={s.payable}
          payableLoading={s.payableLoading}
          totalsByCurrency={s.totalsByCurrency}
          isSuperAdmin={isSuperAdmin}
          connectByUser={s.connectByUser}
          running={s.running}
          onExport={s.exportPayableCsv}
          onMarkPaid={s.openBatchDialog}
          onPayViaStripe={(row) => setConfirmOne(row)}
        />

        <RecentBatchesTable batches={s.batches} batchesLoading={s.batchesLoading} />

        <MarkPaidDialog
          dialogRow={s.dialogRow}
          setDialogRow={s.setDialogRow}
          dialogCommissions={s.dialogCommissions}
          dialogLoading={s.dialogLoading}
          paymentReference={s.paymentReference}
          setPaymentReference={s.setPaymentReference}
          notes={s.notes}
          setNotes={s.setNotes}
          allowMissingTax={s.allowMissingTax}
          setAllowMissingTax={s.setAllowMissingTax}
          taxStatus={s.taxStatus}
          submitting={s.submitting}
          exportBatchCsv={s.exportBatchCsv}
          submitBatch={s.submitBatch}
        />

        <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Run payouts now ({s.env})?</AlertDialogTitle>
              <AlertDialogDescription>
                This will immediately push Stripe transfers to every eligible affiliate. Funds
                leave your Stripe balance. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { setConfirmAll(false); void s.runPayoutAll(); }}>
                Run now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!confirmOne} onOpenChange={(o) => !o && setConfirmOne(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Pay this affiliate via Stripe?</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmOne ? (
                  <>
                    Send{" "}
                    <strong>{formatCommissionAmount(confirmOne.payable_cents, confirmOne.currency)}</strong>{" "}
                    to <span className="font-mono">{maskReferredUserId(confirmOne.affiliate_user_id)}</span>{" "}
                    immediately via Stripe. This bypasses the per-currency floor. Continue?
                  </>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const row = confirmOne;
                  setConfirmOne(null);
                  if (row) void s.runPayoutOne(row);
                }}
              >
                Pay now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TabsContent>
    </Tabs>
  );
}

